import React, { ReactNode, useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { useDispatch } from "react-redux"
import BulkModal from "../../../bulk/modal.component.jsx"
import AccountLimit from "../../../state/account-limit/AccountLimit"
import AccountLimitService from "../../../state/account-limit/AccountLimitService"
import { displayAlert } from "../../../state/alert/reducer"
import CustomField from "../../../state/custom-fields/CustomField"
import CustomFieldService from "../../../state/custom-fields/CustomFieldService"
import { isError } from "../../../state/helpers/Error"
import IntlProps from "../../../state/helpers/IntlProps"
import { TableRowData } from "../../../state/listing/generic-listing/column-definitions/index"
import {
    CUSTOM_FIELD_AVAILABLE_LISTINGS,
    FiltersState,
    FILTERS_DEFAULTS,
    ObjectType,
    ObjectTypeConfig,
    PaginationState,
    PAGINATION_DEFAULTS,
    SortingState,
    SORTING_DEFAULTS,
    StatBoxFilters,
    View
} from "../../../state/listing/generic-listing/index"
import { SearchResultDto } from "../../../state/search/Search"
import { SearchPayload } from "../../../state/search/SearchPayload"
import SearchService, { SearchData } from "../../../state/search/SearchService"
import SearchTotals from "../../../state/search/SearchTotals"
import { AlertVariant } from "../../alert/Alert"
import GlobalHelper from "../../GlobalHelper.jsx"
import { useFetchLabels } from "../../hooks/UseFetchLabels"
import { useFetchPaymentTerms } from "../../hooks/UseFetchPaymentTerms"
import { useFetchTaxes } from "../../hooks/UseFetchTaxes"
import Icon from "../../icon/icon.component.jsx"
import Search from "../../search-filter/search/search"
import LoadingSpinner from "../../spinner/loading.component.jsx"
import Pagination from "./listing-pagination/ListingPagination"
import GenericListingTable from "./listing-table/ListingTable"
import { getColumnDefinitionsWithCustomFields, getCustomFieldHeaders } from "./listing-table/mappers/index"
import {
    getDefaultSortId,
    getDefaultView,
    getObjectTypeConfig
} from "./utils"

interface OwnProps {
    objectType: ObjectType
    additionalObjectTypes?: ObjectType[]
    selectableRows?: boolean
    customBulkAction?: (selectedIds: string[], isAllPagesSelected: boolean) => void
}

const GenericListing = (props: OwnProps & IntlProps) => {
    const dispatch = useDispatch()
    const controller = new AbortController()
    const [loading, setLoading] = useState<boolean>(false)
    const [canRenderListing, setCanRenderListing] = useState<boolean>(false)
    const [config, setConfig] = useState<ObjectTypeConfig>()
    const [accountLimits, setAccountLimits] = useState<AccountLimit>()
    const [totals, setTotals] = useState<SearchTotals>()
    const [data, setData] = useState<TableRowData[]>([])
    const [dataCache] = useState<Map<number, SearchResultDto>>(new Map())
    const [pagination, setPagination] = useState<PaginationState>(PAGINATION_DEFAULTS)
    const [sorting, setSorting] = useState<SortingState>(SORTING_DEFAULTS)
    const [filters, setFilters] = useState<FiltersState>(FILTERS_DEFAULTS)
    const [selectedStatBox, setSelectedStatBox] = useState<string>("")
    const [bulkModal, setBulkModal] = useState<ReactNode>(<></>)
    const [includedAdditionalObjectTypes, setIncludedAdditionalObjectTypes] = useState<ObjectType[]>([])
    const { paymentTermOptions, loadingPaymentTermOptions } = useFetchPaymentTerms(filters.view, controller)
    const { taxOptions, loadingTaxOptions } = useFetchTaxes(filters.view, controller)
    const { labelOptions, loadingLabelOptions } = useFetchLabels(filters.view, controller)

    // FIXME: Weird flicker on initial load
    useEffect(() => {
        setCanRenderListing(false)
        const config = getObjectTypeConfig(props.objectType)
        setConfig(config)

        const fetchAccountLimits = new Promise<AccountLimit>((resolve, reject) => {
            AccountLimitService.Get().then(resolve)
            controller.signal.addEventListener("abort", () => reject(new Error("account limits request aborted")))
        })

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const fetchPreferences = new Promise<any>((resolve, reject) => { // TODO: Add interface for preferences
            GlobalHelper.getListingPreferences(config.listingPreferencesKey, false).then(resolve)
            controller.signal.addEventListener("abort", () => reject())
        })

        const fetchCustomFields = new Promise<CustomField[]>((resolve, reject) => {
            const customFieldAvailableFrom = CUSTOM_FIELD_AVAILABLE_LISTINGS[props.objectType] ?? ""
            if (customFieldAvailableFrom) {
                CustomFieldService.getAvailableFor(customFieldAvailableFrom).then(resolve)
            } else {
                resolve([])
            }
            controller.signal.addEventListener("abort", () => reject(new Error("listing preferences request aborted")))
        })

        Promise.all([
            fetchAccountLimits,
            fetchPreferences,
            fetchCustomFields,
        ]).then(([
            accountLimits,
            listingPreferences,
            customFields,
        ]) => {
            setAccountLimits(accountLimits)
            setSelectedStatBox(listingPreferences.statBox ?? "")
            if (
                listingPreferences.includeCreditMemos
                && props.additionalObjectTypes?.find(type => type === ObjectType.CREDIT_MEMOS)
            ) {
                setIncludedAdditionalObjectTypes([ObjectType.CREDIT_MEMOS])
            }

            if(customFields && !config.customFieldHeaders) {
                config.customFieldHeaders = getCustomFieldHeaders(customFields)
                config.columnDefinitions = getColumnDefinitionsWithCustomFields(config, customFields)
            }
    
            const initialSorting: SortingState = {
                sortField: listingPreferences.sortField || getDefaultSortId(props.objectType),
                asc: listingPreferences.asc || SORTING_DEFAULTS.asc,
                nestedSortFilters: listingPreferences.nestedSortFilters || {},
            }
            setSorting(initialSorting)
    
            const initialPagination: PaginationState = {
                ...PAGINATION_DEFAULTS,
                activePage: listingPreferences.page || PAGINATION_DEFAULTS.activePage,
                pageSize: listingPreferences.pageSize || PAGINATION_DEFAULTS.pageSize,
            }
            setPagination(initialPagination)
    
            const initialFilters: FiltersState = {
                ...FILTERS_DEFAULTS,
                renderedKeywords: listingPreferences.keywords || FILTERS_DEFAULTS.renderedKeywords,
                renderedFilters: listingPreferences.filters || FILTERS_DEFAULTS.renderedFilters,
                savedFilterId: listingPreferences.savedFilterId || FILTERS_DEFAULTS.savedFilterId,
                view: listingPreferences.view || getDefaultView(props.objectType),
            }
            setFilters(initialFilters)
            setCanRenderListing(true)
        }).catch(err => console.warn(err))
        
        return () => controller.abort()
    }, [])

    const fetchListingData = async (
        pagination: PaginationState,
        sorting: SortingState,
        filters: FiltersState,
        includeAdditionalObjectType: ObjectType[],
        refreshTotals = false,
    ) => {
        if (!canRenderListing) { return }

        let listingSearchResult
        const searchParams = getSearchParams(pagination, sorting, filters, includeAdditionalObjectType)

        if (dataCache.has(pagination.activePage)) {
            listingSearchResult = dataCache.get(pagination.activePage)
        } else {

            switch (props.objectType) {
            case ObjectType.ITEM_REPORT:
                searchParams.aggregationField = "productId"
                listingSearchResult = await SearchService.itemReportListingSearch(searchParams)
                break
            default:
                listingSearchResult = await SearchService.listingSearch(searchParams)
            }

            if (isError(listingSearchResult)) {
                dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load search results" }))
            } else {
                dataCache.set(pagination.activePage, listingSearchResult)
            }

        }

        if (refreshTotals) {
            const totals = await SearchService.mainSearchTotals(searchParams)

            if (isError(totals)) {
                dispatch(displayAlert({ type: AlertVariant.DANGER, message: "Error: Could not load search totals" }))
            } else {
                setTotals(totals as SearchTotals)
            }
        }

        setPagination({ ...pagination, totalItemCount: listingSearchResult.metadata.totalItemsCount })
        setData(listingSearchResult.data)
    }

    const getSearchParams = (
        pagination: PaginationState,
        sorting: SortingState,
        filters: FiltersState,
        includedAdditionalObjectTypes: ObjectType[],
    ): SearchPayload => {
        if (!config) {
            throw new Error("Error: Tried to fetch data before loading object type configuration.")
        }

        return {
            objects: [config.searchObjectType, ...includedAdditionalObjectTypes],
            fields: filters.fields,
            filters: {
                ...filters.filters,
                ...(config.additionalFilters ?? {}),
            },
            keywords: filters.keywords,
            lightResult: filters.lightResult,
            totals: filters.totals,
            activePage: pagination.activePage,
            pageSize: pagination.pageSize,
            sortAsc: sorting.asc,
            sortByField: sorting.sortField,
            nestedSortFilters: sorting.nestedSortFilters
        }
    }

    const savePreferences = (
        pagination: PaginationState,
        sorting: SortingState,
        filters: FiltersState,
        includedAdditionalObjectTypes: ObjectType[]
    ) => {
        if (!config) {
            throw new Error("Error: Tried to save preferences before loading object type configuration.")
        }

        GlobalHelper.saveListingPreferences(
            config.listingPreferencesKey,
            pagination.activePage,
            pagination.pageSize,
            filters.renderedKeywords,
            selectedStatBox,
            includedAdditionalObjectTypes.find(type => type === ObjectType.CREDIT_MEMOS),
            false,
            sorting.sortField,
            sorting.asc,
            filters.renderedFilters,
            filters.savedFilterId,
            filters.view,
            sorting.nestedSortFilters,
        )
    }

    const handlePaginationChange = async (newPagination: PaginationState) => {
        setLoading(true)
        if (newPagination.pageSize !== pagination.pageSize) {
            dataCache.clear()
        }
        setPagination(newPagination)
        savePreferences(
            newPagination,
            sorting,
            filters,
            includedAdditionalObjectTypes,
        )
        await fetchListingData(newPagination, sorting, filters, includedAdditionalObjectTypes)
        setLoading(false)
    }

    const handleSortingChange = async (sortField: string, asc: boolean, nestedSortFilters?: {[key:string]: string}) => {
        setLoading(true)
        dataCache.clear()
        const newSorting: SortingState = { sortField, asc, nestedSortFilters }
    
        setSorting(newSorting)
        savePreferences(
            pagination,
            newSorting,
            filters,
            includedAdditionalObjectTypes,
        )
        await fetchListingData(pagination, newSorting, filters, includedAdditionalObjectTypes)
        setLoading(false)
    }

    const handleFiltersChange = async (
        renderedKeywords: string,
        renderedFilters: object,
        savedFilterId: string,
        searchData: SearchData,
        view: View,
        savePrefs: boolean,
        refreshTotals: boolean,
        resetStatBox: boolean,
    ) => {
        if (!config) {
            throw new Error("Error: Tried to change filters before loading object type configuration.")
        }

        setLoading(true)
        dataCache.clear()
        const newFilters: FiltersState = Object.assign(
            filters,
            {
                renderedFilters,
                renderedKeywords,
                savedFilterId,
                view: {
                    ...view,
                    columns: view.columns.length ? view.columns : getDefaultView(config.objectType).columns
                },
                filters: searchData.filters,
                keywords: searchData.keywords,
                fields: searchData.fields,
                totals: searchData.totals,
                lightResult: searchData.lightResult,
            }
        )
        
        if (savePrefs) {
            setFilters(newFilters)
            savePreferences(
                pagination,
                sorting,
                newFilters,
                includedAdditionalObjectTypes,
            )
        }

        if (resetStatBox) {
            setSelectedStatBox("")
        }

        await fetchListingData({ ...pagination, activePage: 1 }, sorting, newFilters, includedAdditionalObjectTypes, refreshTotals)
        setLoading(false)
    }

    const handleStatBoxChange = async (id: string, statBoxFilters: StatBoxFilters, previousFilters: StatBoxFilters) => {
        const renderedFilters: object = Object.assign({}, filters.renderedFilters)
            
        Object.keys(previousFilters).forEach(key => {
            delete renderedFilters[key]
        })

        Object.assign(renderedFilters, statBoxFilters)

        setSelectedStatBox(id)
        setFilters(Object.assign(
            filters,
            { renderedFilters }
        ))
    }

    const handleOpenBulkModal = (type: string, selectedIds: string[], isAllPagesSelected: boolean) => {
        if (props.customBulkAction) {
            props.customBulkAction(selectedIds, isAllPagesSelected)
            return
        }

        const searchParams = getSearchParams(pagination, sorting, filters, includedAdditionalObjectTypes)
        const selectionCount = isAllPagesSelected ? pagination.totalItemCount : selectedIds.length
    
        if (!isAllPagesSelected) {
            searchParams.fields = ["_id"]
            searchParams.filters = {
                _id: selectedIds
            }
            searchParams.keywords = "*"
        }

        setBulkModal(
            <BulkModal
                filters={searchParams}
                operationType={type}
                numberOfItemsSelected={selectionCount}
                object={props.objectType}
                onClose={() => setBulkModal(<></>)}
            />
        )
    }

    const handleToggleAdditionalObjectType = async (objectType: ObjectType) => {
        const index = includedAdditionalObjectTypes.indexOf(objectType)
        const updatedObjectTypes = [...includedAdditionalObjectTypes]

        if (index === -1) {
            updatedObjectTypes.push(objectType)
        } else {
            updatedObjectTypes.splice(index, 1)
        }

        setLoading(true)
        setIncludedAdditionalObjectTypes(updatedObjectTypes)
        savePreferences(
            pagination,
            sorting,
            filters,
            updatedObjectTypes,
        )
        dataCache.clear()
        await fetchListingData({ ...pagination, activePage: 1 }, sorting, filters, updatedObjectTypes)
        setLoading(false)
    }

    const handleApplyView = async (view: View) => {
        setLoading(true)

        const newFilters: FiltersState = Object.assign(
            filters,
            { view }
        )
        setFilters(newFilters)
        savePreferences(
            pagination,
            sorting,
            newFilters,
            includedAdditionalObjectTypes,
        )

        await fetchListingData(pagination, sorting, newFilters, includedAdditionalObjectTypes)
        setLoading(false)
    }

    const refetchListingData = async () => {
        setLoading(true)
        //Wait 2 seconds before refetch to allow time for the document to appear on Elastic
        setTimeout(async () => {
            dataCache.clear()
            await fetchListingData(pagination, sorting, filters, includedAdditionalObjectTypes, true)
            setLoading(false)
        }, 2000)
    }

    const isAdditionalObjectTypeIncluded = (objectType: ObjectType): boolean => {
        return includedAdditionalObjectTypes.indexOf(objectType) !== -1
    }

    const getStatBoxes = (): ReactNode => {
        const Component = config?.statBoxComponent
        return Component
            ? (
                <div className="row ui-has-modules px-1 pt-0">
                    <Component.default
                        selected={selectedStatBox}
                        onChange={handleStatBoxChange}
                    />
                </div>
            )
            : (
                <></>
            )
    }

    const getExportModal = (): ReactNode => {
        const Component = config?.exportModalComponent
        return Component
            ? (
                <Component.default
                    exportType={props.objectType}
                    filters={getSearchParams(pagination, sorting, filters, includedAdditionalObjectTypes)}
                />
            )
            : (
                <></>
            )
    }

    const getCreationButton = (): ReactNode => {
        const Component = config?.creationButtonComponent

        return Component
            ? (
                <Component.default accountLimits={accountLimits} onCloseCallback={refetchListingData}/>
            )
            : (
                <></>
            )
    }

    const showPagination = ![ObjectType.ITEM_REPORT].includes(props.objectType)

    return (
        <div className="generic-listing">
            {(!canRenderListing || !config) && (
                <LoadingSpinner />
            )}
            {canRenderListing && config && (
                <>
                    {bulkModal}
                    {getStatBoxes()}
                    <Search
                        typeOfSearch={config.typeOfSearch || props.objectType}
                        searchKeywords={filters.renderedKeywords}
                        filtersToUse={filters.renderedFilters}
                        object={[config.searchObjectType, ...includedAdditionalObjectTypes]}
                        placeholder={props.intl.formatMessage(config.searchPlaceholder)}
                        savedFilterId={filters.savedFilterId}
                        view={filters.view}
                        onChange={handleFiltersChange}
                    >
                        {getCreationButton()}
                    </Search>
                    <div className="mt-3">
                        <GenericListingTable
                            config={config}
                            data={data}
                            totals={totals}
                            totalItemsCount={pagination.totalItemCount}
                            view={filters.view}
                            sorting={sorting}
                            selectableRows={props.selectableRows ?? false}
                            additionalOptions={{
                                paymentTermOptions: paymentTermOptions || [],
                                taxOptions: taxOptions || [],
                                labelOptions: labelOptions || [],
                            }}
                            loadingState={{
                                loadingData: loading,
                                loadingPaymentTermOptions,
                                loadingTaxOptions,
                                loadingLabelOptions,
                            }}
                            onSortingChange={handleSortingChange}
                            onOpenBulkModal={handleOpenBulkModal}
                            onApplyView={handleApplyView}
                            filters={filters}
                        />
                    </div>
                    <div className="d-flex align-items-center">
                        { showPagination &&
                            <Pagination
                                activePage={pagination.activePage}
                                pageSize={pagination.pageSize}
                                pageSizeMax={pagination.pageSizeMax}
                                totalItemCount={pagination.totalItemCount}
                                onPaginationChange={handlePaginationChange}
                                showPageSize
                            />
                        }
                        <div className="mr-1">
                            {props.additionalObjectTypes?.map(objectType => (
                                <button
                                    key={objectType}
                                    className={
                                        `btn btn-sm mr-2 ${isAdditionalObjectTypeIncluded(objectType) ? "btn-secondary" : "btn-outline-secondary"}`
                                    }
                                    onClick={() => handleToggleAdditionalObjectType(objectType)}>
                                    {isAdditionalObjectTypeIncluded(objectType) && (
                                        <Icon value="check" className="mr-1" />
                                    )}
                                    <FormattedMessage {...getObjectTypeConfig(objectType).displayString} />
                                </button>
                            ))}
                            {config.exportModalComponent && data.length > 0 && (
                                getExportModal()
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default injectIntl(GenericListing)


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/GenericListing.tsx